import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Container, Divider, LinearProgress,
  Stack, TextField, Typography,
} from '@mui/material';
import { Card, Pill, SectionHeader } from '../components/shared';
import {
  listPostings, listDrafts, createDraft, generateTab, previewDraft,
  updateItem, updateOpening, mintShare, revokeShare, rotateShare, getTask,
} from '../services/shareAdminService';
import { SAGE, CREAM, WARM } from '../theme';

/**
 * Create Share Link — admin only.
 *
 * The flow is draft-first: build and review a draft with no token, then mint.
 * Nothing here is reachable by anyone until the last step, which is the point —
 * a link is the one thing you can't take back cleanly.
 *
 * The middle of this page is the part that justified building it. Generation
 * fills requirements and claims but deliberately leaves the openings,
 * `position` statements and the `candid` sentence blank, because those are the
 * author's own words. Before this existed, writing them meant editing JSON in
 * S3 — so in practice they'd have gone out blank.
 */

const POLL_MS = 3000;

const ShareAdminPage = () => {
  const [postings, setPostings] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [selected, setSelected] = useState(null);      // content_id
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [task, setTask] = useState(null);             // {task_id, status, progress}
  const [minted, setMinted] = useState(null);
  const [newContentId, setNewContentId] = useState('');

  const refresh = useCallback(async () => {
    const [p, d] = await Promise.all([listPostings(), listDrafts()]);
    setPostings(p.postings || []);
    setDrafts(d.drafts || []);
    return d.drafts || [];
  }, []);

  useEffect(() => {
    (async () => {
      try {
        await refresh();
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [refresh]);

  const loadPreview = useCallback(async (contentId, { keepVisible = false } = {}) => {
    setSelected(contentId);
    // Blanking the preview unmounts the editor, which throws away every
    // in-progress edit in it. On a refresh after saving one field we keep the
    // old tree on screen and swap it when the new payload lands.
    if (!keepVisible) setPreview(null);
    try {
      setPreview(await previewDraft(contentId));
    } catch (e) {
      setError(e.message);
    }
  }, []);

  // Generation runs for minutes as a background task; poll until it settles.
  useEffect(() => {
    if (!task?.task_id || ['completed', 'failed'].includes(task.status)) return undefined;
    const timer = setInterval(async () => {
      try {
        const t = await getTask(task.task_id);
        setTask(t);
        if (t.status === 'completed') {
          await refresh();
          if (selected) await loadPreview(selected);
        } else if (t.status === 'failed') {
          setError(t.error || 'Generation failed');
        }
      } catch (e) {
        setError(e.message);
      }
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [task, refresh, selected, loadPreview]);

  const draft = useMemo(
    () => drafts.find((d) => d.content_id === selected) || null,
    [drafts, selected]
  );

  const runGenerate = async (postingId) => {
    setError(null);
    try {
      setTask(await generateTab(selected, postingId));
    } catch (e) {
      setError(e.message);
    }
  };

  const onMint = async () => {
    setError(null);
    try {
      const label = window.prompt('Label for your own reference (who is this for?)');
      if (!label) return;
      const result = await mintShare(selected, { label });
      if (result.ok) {
        setMinted(result);
        await refresh();
      } else {
        setError(`${result.unwritten.length} statement(s) still need writing.`);
      }
    } catch (e) {
      setError(e.message);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '60vh' }}>
        <CircularProgress sx={{ color: SAGE[400] }} />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Typography variant="h4" sx={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600, color: SAGE[800] }}>
        Create Share Link
      </Typography>
      <Typography sx={{ fontSize: 13.5, color: SAGE[500], mt: 0.5, maxWidth: '70ch' }}>
        Build a draft against a posting, write the parts only you can write, preview it
        as the recipient will see it, then mint the link. Nothing is reachable until
        you mint.
      </Typography>

      {error && <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {minted && (
        <Alert severity="success" sx={{ mt: 2 }} onClose={() => setMinted(null)}>
          <Typography sx={{ fontWeight: 600, fontSize: 13.5 }}>Link created — copy it now</Typography>
          <Typography sx={{ fontFamily: 'monospace', fontSize: 12.5, mt: 0.5, wordBreak: 'break-all' }}>
            {`${window.location.origin}/profileshare/${minted.slug}/${minted.token}`}
          </Typography>
          <Typography sx={{ fontSize: 11.5, mt: 0.5 }}>
            Shown once — only a hash is stored, so this can't be recovered.
          </Typography>
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 3, mt: 3, alignItems: 'flex-start', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
        {/* Drafts */}
        <Card sx={{ flex: { xs: '1 1 100%', md: '0 0 300px' }, p: 2 }}>
          <SectionHeader>Drafts</SectionHeader>
          <Stack spacing={1} sx={{ mt: 1 }}>
            {drafts.map((d) => (
              <Box
                key={d.content_id}
                component="button"
                onClick={() => loadPreview(d.content_id)}
                sx={{
                  textAlign: 'left', border: `1px solid ${d.content_id === selected ? SAGE[400] : SAGE[100]}`,
                  bgcolor: d.content_id === selected ? CREAM[200] : CREAM[100],
                  borderRadius: 2, p: 1.25, cursor: 'pointer', font: 'inherit', width: '100%',
                }}
              >
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: SAGE[800] }}>
                  {d.content_id}
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: SAGE[500] }}>
                  {d.tabs.length} tab{d.tabs.length === 1 ? '' : 's'}
                  {d.shares.length > 0 && ` · ${d.shares.filter((s) => !s.revoked).length} live link(s)`}
                </Typography>
                <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
                  {d.unwritten.length > 0 && (
                    <Pill preset="warm" label={`${d.unwritten.length} to write`}
                      sx={{ height: 18, fontSize: 10 }} />
                  )}
                  {d.stale_context?.length > 0 && (
                    <Pill preset="neutral" label={`${d.stale_context.length} new context`}
                      sx={{ height: 18, fontSize: 10 }} />
                  )}
                </Stack>
              </Box>
            ))}
            {drafts.length === 0 && (
              <Typography sx={{ fontSize: 12.5, color: SAGE[400], fontStyle: 'italic' }}>
                No drafts yet.
              </Typography>
            )}
          </Stack>

          <Divider sx={{ my: 2 }} />
          <SectionHeader>New draft</SectionHeader>
          <TextField
            value={newContentId}
            onChange={(e) => setNewContentId(e.target.value)}
            placeholder="e.g. sageox"
            size="small" fullWidth sx={{ mt: 1 }}
          />
          <Button
            fullWidth size="small" disabled={!newContentId.trim()}
            onClick={async () => {
              await createDraft({ content_id: newContentId.trim() });
              setNewContentId('');
              await refresh();
              loadPreview(newContentId.trim());
            }}
            sx={{ mt: 1, textTransform: 'none', bgcolor: SAGE[500], color: '#fff',
                  '&:hover': { bgcolor: SAGE[600] },
                  '&.Mui-disabled': { bgcolor: SAGE[100], color: SAGE[300] } }}
          >
            Create draft
          </Button>
        </Card>

        {/* Editor */}
        <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
          {!selected ? (
            <Card sx={{ p: 3 }}>
              <Typography sx={{ fontSize: 13.5, color: SAGE[500] }}>
                Pick a draft, or create one.
              </Typography>
            </Card>
          ) : (
            <Stack spacing={2}>
              <Card sx={{ p: 2 }}>
                <SectionHeader>Generate a tab from a posting</SectionHeader>
                <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
                  {postings.map((p) => (
                    <Button
                      key={p.id} size="small"
                      disabled={Boolean(task && !['completed', 'failed'].includes(task.status))}
                      onClick={() => runGenerate(p.id)}
                      sx={{ textTransform: 'none', fontSize: 12, border: `1px solid ${SAGE[200]}`,
                            color: SAGE[600] }}
                    >
                      {p.title}
                      {draft?.tabs.some((t) => t.id === p.tab_id) && ' ↻'}
                    </Button>
                  ))}
                </Stack>
                {draft?.stale_context?.length > 0 && (
                  <Typography sx={{ mt: 1.5, fontSize: 12, color: SAGE[600], lineHeight: 1.6 }}>
                    {draft.stale_context.length} claim
                    {draft.stale_context.length === 1 ? ' has' : 's have'} context you added on the
                    profile page that this tab hasn't picked up. Context is condensed against each
                    requirement when a tab is generated, so regenerate to pull it in — your
                    openings and statements are kept.
                  </Typography>
                )}
                {task && !['completed', 'failed'].includes(task.status) && (
                  <Box sx={{ mt: 1.5 }}>
                    <Typography sx={{ fontSize: 12, color: SAGE[500] }}>
                      {task.progress?.stage || 'starting'}… this takes a few minutes.
                    </Typography>
                    <LinearProgress variant="determinate" value={task.progress?.percent || 5}
                      sx={{ mt: 0.5, height: 5, borderRadius: 3 }} />
                  </Box>
                )}
              </Card>

              {preview && <DraftEditor
                contentId={selected}
                preview={preview}
                onChanged={() => loadPreview(selected, { keepVisible: true })}
              />}

              <Card sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ flex: 1, minWidth: 200 }}>
                  <SectionHeader>Mint the link</SectionHeader>
                  <Typography sx={{ fontSize: 12, color: SAGE[500], mt: 0.5 }}>
                    {draft?.unwritten?.length
                      ? `${draft.unwritten.length} statement(s) still need your words.`
                      : 'Ready to send.'}
                  </Typography>
                </Box>
                <Button
                  onClick={onMint}
                  disabled={!draft?.tabs?.length}
                  sx={{ textTransform: 'none', bgcolor: SAGE[500], color: '#fff', px: 3,
                        '&:hover': { bgcolor: SAGE[600] },
                        '&.Mui-disabled': { bgcolor: SAGE[100], color: SAGE[300] } }}
                >
                  Create share link
                </Button>
              </Card>

              {draft?.shares?.length > 0 && (
                <Card sx={{ p: 2 }}>
                  <SectionHeader>Links from this draft</SectionHeader>
                  <Stack spacing={1} sx={{ mt: 1 }}>
                    {draft.shares.map((s) => (
                      <ShareRow key={s.share_id} share={s} onChanged={refresh} />
                    ))}
                  </Stack>
                </Card>
              )}
            </Stack>
          )}
        </Box>
      </Box>
    </Container>
  );
};

/**
 * One minted link: its URL, and what you can do to it.
 *
 * The URL is shown rather than revealed once at mint. A list of links you
 * cannot open is not much of a list — you could not re-send one, or look at
 * what a recipient is actually seeing.
 */
const ShareRow = ({ share, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const url = share.token
    ? `${window.location.origin}/profileshare/${share.slug || 'profile'}/${share.token}`
    : '';

  const act = async (fn) => {
    setBusy(true);
    try { await fn(); await onChanged(); } finally { setBusy(false); }
  };

  return (
    <Box sx={{ p: 1.25, borderRadius: 2, bgcolor: CREAM[100], border: `1px solid ${SAGE[100]}` }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography sx={{ fontSize: 12.5, color: SAGE[700], flex: 1 }}>{share.label}</Typography>
        <Pill preset={share.revoked ? 'rose' : 'sage'}
          label={share.revoked ? 'revoked' : 'live'} sx={{ height: 18, fontSize: 10 }} />
      </Box>

      {url ? (
        <Typography
          sx={{
            mt: 0.5, fontFamily: 'monospace', fontSize: 11, color: SAGE[500],
            wordBreak: 'break-all', textDecoration: share.revoked ? 'line-through' : 'none',
          }}
        >
          {url}
        </Typography>
      ) : (
        <Typography sx={{ mt: 0.5, fontSize: 11, color: SAGE[400], fontStyle: 'italic' }}>
          Minted before links were stored — the original is unrecoverable. Rotate to
          issue a new one; the share keeps its questions and sessions.
        </Typography>
      )}

      <Stack direction="row" spacing={1} sx={{ mt: 0.75, flexWrap: 'wrap' }}>
        {url && !share.revoked && (
          <>
            <Button size="small" disabled={busy}
              onClick={() => { navigator.clipboard?.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
              sx={{ textTransform: 'none', fontSize: 11.5, color: SAGE[600] }}>
              {copied ? 'Copied' : 'Copy link'}
            </Button>
            <Button size="small" href={url} target="_blank" rel="noopener noreferrer"
              sx={{ textTransform: 'none', fontSize: 11.5, color: SAGE[600] }}>
              Open
            </Button>
          </>
        )}
        {!share.revoked && (
          <Button size="small" disabled={busy}
            onClick={() => act(() => rotateShare(share.share_id))}
            sx={{ textTransform: 'none', fontSize: 11.5, color: SAGE[500] }}>
            Rotate
          </Button>
        )}
        {!share.revoked && (
          <Button size="small" disabled={busy}
            onClick={() => act(() => revokeShare(share.share_id))}
            sx={{ textTransform: 'none', fontSize: 11.5, color: WARM.rose }}>
            Revoke
          </Button>
        )}
      </Stack>
    </Box>
  );
};

/**
 * The review surface: every requirement as the recipient will see it, with the
 * blanks generation left behind editable in place.
 */
const DraftEditor = ({ contentId, preview, onChanged }) => {
  const fitTabs = (preview.tabs || []).filter((t) => t.kind === 'fit');
  const [active, setActive] = useState(fitTabs[0]?.id || null);
  const tab = fitTabs.find((t) => t.id === active) || fitTabs[0];

  if (!tab) {
    return (
      <Card sx={{ p: 3 }}>
        <Typography sx={{ fontSize: 13.5, color: SAGE[500] }}>
          No tabs yet — generate one from a posting above.
        </Typography>
      </Card>
    );
  }

  return (
    <Card sx={{ p: 2.5 }}>
      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
        {fitTabs.map((t) => (
          <Chip
            key={t.id} label={t.label} onClick={() => setActive(t.id)}
            sx={{
              bgcolor: t.id === tab.id ? SAGE[500] : CREAM[200],
              color: t.id === tab.id ? '#fff' : SAGE[600],
              fontFamily: "'Outfit', sans-serif",
            }}
          />
        ))}
      </Stack>

      <EditableBlock
        label="Opening — what you read their posting to really be asking for"
        value={tab.opening}
        onSave={(v) => updateOpening(contentId, tab.id, v).then(onChanged)}
      />

      {(tab.sections || []).map((section) => (
        <Box key={section.id || section.title} sx={{ mt: 3 }}>
          <Typography sx={{ fontFamily: "'Outfit', sans-serif", fontSize: 12.5, fontWeight: 600,
                            color: SAGE[600], pb: 0.75, borderBottom: `2px solid ${SAGE[300]}` }}>
            {section.title}
          </Typography>
          {(section.items || []).map((item) => (
            <Box key={item.id} sx={{ py: 1.5, borderBottom: `1px solid ${SAGE[100]}` }}>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <Typography sx={{ flex: 1, fontSize: 14, fontWeight: 500, color: SAGE[800], lineHeight: 1.5 }}>
                  {item.verbatim === false ? item.quote : `“${item.quote}”`}
                </Typography>
                <Typography sx={{ fontSize: 10.5, color: item.kind === 'candid' ? WARM.rose : SAGE[400],
                                  width: 74, textAlign: 'right', flexShrink: 0, mt: '3px' }}>
                  {{ evidence: 'Evidence', position: 'Position', answer: 'Answer', candid: 'Not a match' }[item.kind]}
                </Typography>
              </Stack>

              {item.kind === 'evidence' ? (
                <Stack spacing={0.5} sx={{ mt: 1, pl: 2 }}>
                  {(item.claims || []).map((c) => (
                    <Box key={c.id}>
                      <Typography sx={{ fontSize: 12.5, color: SAGE[700] }}>
                        {c.claim}
                        <Typography component="span" sx={{ fontSize: 11, color: SAGE[400], ml: 1 }}>
                          {[c.source, c.dates].filter(Boolean).join(' · ')}
                        </Typography>
                      </Typography>
                      {c.context ? (
                        <Typography sx={{ fontSize: 11.5, color: SAGE[600], pl: 1.25,
                                          borderLeft: `2px solid ${SAGE[200]}`, mt: 0.4 }}>
                          {c.context}
                        </Typography>
                      ) : (
                        <Typography sx={{ fontSize: 11, color: SAGE[400], fontStyle: 'italic', mt: 0.25 }}>
                          no context — add it on the profile page, then regenerate
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Box sx={{ mt: 1, pl: 2 }}>
                  <EditableBlock
                    label={item.kind === 'candid'
                      ? 'Name the gap plainly — this is what makes everything else credible'
                      : 'Your answer, in your own words'}
                    value={item.statement}
                    onSave={(v) => updateItem(contentId, tab.id, item.id, { statement: v }).then(onChanged)}
                  />
                </Box>
              )}
            </Box>
          ))}
        </Box>
      ))}
    </Card>
  );
};

/** A field you edit in place; saves explicitly so a stray blur can't wipe it. */
const EditableBlock = ({ label, value, onSave }) => {
  const [draft, setDraft] = useState(value || '');
  const [saving, setSaving] = useState(false);

  // Adopt a new server value only where the field has no unsaved edit. Saving
  // one field refetches the whole draft, and an unconditional sync here meant
  // that refetch overwrote whatever had been typed into every other field.
  const lastServerValue = useRef(value || '');
  useEffect(() => {
    setDraft((prev) => (prev === lastServerValue.current ? (value || '') : prev));
    lastServerValue.current = value || '';
  }, [value]);

  const dirty = draft !== (value || '');

  return (
    <Box sx={{ mt: 1 }}>
      <Typography sx={{ fontSize: 11, color: (value || '').trim() ? SAGE[400] : WARM.rose, mb: 0.4 }}>
        {label}{!(value || '').trim() && ' — still blank'}
      </Typography>
      <TextField
        value={draft} onChange={(e) => setDraft(e.target.value)}
        multiline minRows={2} fullWidth size="small"
        sx={{ '& .MuiOutlinedInput-root': { bgcolor: CREAM[50], fontSize: 13 } }}
      />
      {dirty && (
        <Button
          size="small" disabled={saving}
          onClick={async () => { setSaving(true); try { await onSave(draft); } finally { setSaving(false); } }}
          sx={{ mt: 0.5, textTransform: 'none', fontSize: 12, bgcolor: SAGE[500], color: '#fff',
                '&:hover': { bgcolor: SAGE[600] } }}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      )}
    </Box>
  );
};

export default ShareAdminPage;
